"use client"

/**
 * VideoSliderAd — a small video ad that automatically slides into a screen
 * corner and plays while the visitor browses, sitewide (mounted once in
 * app/layout.tsx, same as StickyBannerAd/PopunderInjector). Self-contained
 * and self-positioning on ExoClick's side — unlike StickyBannerAd's static
 * banner, there's no single "native size" to render at exactly, so this
 * uses a reasonable bounding box sized to a typical video-slider footprint,
 * positioned to match the zone's own bottom-right configuration in the
 * ExoClick dashboard.
 *
 * This used to rely on the creative's own close button, on the assumption
 * its script "handles... its own close button inside the creative." It
 * doesn't reliably: reported live, that in-iframe X either didn't register
 * clicks on narrower layouts or — worse, confirmed separately on a sibling
 * placement — some creatives self-navigate on click rather than closing
 * (see lib/ad-embed.ts / the allow-popups fix for why that self-navigation
 * happens at all). There was never a way to dismiss this widget that didn't
 * depend on the ad's own script behaving. Now there is, below.
 *
 * AUTO_DISMISS_MS — see the matching comment on InstantMessageAd.tsx for
 * the full reasoning (no expiry at all previously, "even when it goes away,
 * they just sit there"). Longer here than the two notification-style
 * siblings since this is video content, which naturally runs longer.
 */
const AUTO_DISMISS_MS = 60_000
const NATIVE_W = 340
const NATIVE_H = 220

import { useEffect, useRef, useState } from "react"
import { useAdStore } from "@/lib/ad-store"
import { useHasConsent } from "@/components/cookie-consent"
import { useVRStore } from "@/lib/vr-store"
import { useAdTier } from "@/lib/ad-tier"
import { useAdFrequencyStore } from "@/lib/ad-frequency-store"
import { isAdCooldownExpired, isAdTestModeOn, markAdShown } from "@/lib/ad-cooldown"
import { isGlobalAdsOff } from "@/lib/global-ads-off"
import { extractExoclickZoneId, exoclickIframeSrc } from "@/lib/ad-embed"
import { useAdTriggerStore } from "@/lib/ad-trigger-store"
import { isSkipAdsModalActive } from "@/lib/skip-ads"
import { useCornerAdPlacement } from "@/lib/ad-placement"
import { useFixedCornerAdScale } from "@/lib/hooks/use-fixed-corner-ad-scale"
import { useTranslations } from "next-intl"

interface AdData {
  zone_id: string
  zone_type: string
  embed_code: string | null
}

export function VideoSliderAd() {
  const t = useTranslations("videoSliderAd")
  const hasConsent = useHasConsent()
  // Suppressed in VR (app/vr/page.tsx) — a fixed corner widget popping up
  // over a full-screen VR view has no sensible place to render. The VR page
  // gets Instant Message instead, see lib/ad-placement.ts.
  const isVRMode = useVRStore((s) => s.isVRMode)
  const { isPremiumUser } = useAdStore()
  const { scale, scaledWidth, scaledHeight } = useFixedCornerAdScale(NATIVE_W, NATIVE_H)
  // Force Ads test mode escape hatch — see the matching comment on
  // NetworkDisplayAd.tsx. Admin/super_admin accounts always read
  // isPremiumUser: true, so without this, Force Ads could never actually
  // force this widget to show for its only realistic tester.
  // See the matching comment on StickyBannerAd.tsx — SkipAdsModal's forced
  // trigger previously only bypassed the "enabled" check, not this premium
  // gate, so it silently rendered nothing for a premium/admin tester
  // outside Force Ads mode despite the modal genuinely triggering it.
  const effectivePremiumUser = (isPremiumUser || isGlobalAdsOff()) && !isAdTestModeOn() && !isSkipAdsModalActive()
  const adTier = useAdTier()
  const [adData, setAdData] = useState<AdData | null>(null)
  const [dismissed, setDismissed] = useState(false)
  const triggerCount = useAdTriggerStore((s) => s.videoSlider)
  const lastTriggerRef = useRef(0)
  // Only allowed on the pages lib/ad-placement.ts lists (Goonfolio). A manual
  // harness trigger or the Skip Ads modal shows it anywhere.
  const placementOk = useCornerAdPlacement("video_slider")
  const forcedRef = useRef(false)

  useEffect(() => {
    const manuallyTriggered = triggerCount > lastTriggerRef.current
    lastTriggerRef.current = triggerCount
    if (manuallyTriggered) forcedRef.current = true
    if (!hasConsent || effectivePremiumUser || isVRMode) return
    if (!placementOk && !forcedRef.current && !isSkipAdsModalActive()) return
    // Wait for the real config before reading the on/off switch and cooldown
    // -- see the matching comment on StickyBannerAd.tsx.
    let ignore = false
    useAdFrequencyStore.getState().fetchConfig().then(() => {
      if (ignore) return
      if (!manuallyTriggered && !useAdFrequencyStore.getState().isEnabled("video_slider_ads") && !isSkipAdsModalActive()) return
      if (!manuallyTriggered) {
        const cooldown = useAdFrequencyStore.getState().getCooldownMinutes("video_slider_ads")
        if (!isAdCooldownExpired("video_slider", cooldown)) return
      }
      if (manuallyTriggered) setDismissed(false)

      fetch(`/api/ads/network-grid-ad?type=video_slider&tier=${adTier}${isAdTestModeOn() ? "&preview=1" : ""}`)
        .then((r) => (r.ok ? r.json() : null))
        .then((d) => {
          if (ignore) return
          if (d?.zone_id) {
            setAdData(d)
            markAdShown("video_slider")
          }
        })
        .catch(() => {})
    })
    return () => {
      ignore = true
    }
  }, [hasConsent, effectivePremiumUser, adTier, triggerCount, placementOk])

  useEffect(() => {
    if (!adData) return
    const timer = setTimeout(() => setDismissed(true), AUTO_DISMISS_MS)
    return () => clearTimeout(timer)
  }, [adData])

  if (!hasConsent || effectivePremiumUser || isVRMode || !adData || dismissed) return null
  if (!placementOk && !forcedRef.current && !isSkipAdsModalActive()) return null

  const zoneId = adData.zone_type === "js_embed" ? extractExoclickZoneId(adData.embed_code) : null
  if (!zoneId) return null

  // Bottom-RIGHT, to match the direction the creative itself animates from.
  // The ExoClick zone is configured bottom-right on their side (see the file
  // header), so the video slides out from the RIGHT edge — but this wrapper
  // used to be pinned bottom-LEFT, which made the creative appear to slide
  // in from the wrong side of its own container. Swapped with
  // InstantMessageAd (now bottom-left) rather than just moved, so all four
  // sitewide widgets still occupy one distinct corner each:
  // StickyBannerAd top-left, InPagePushAd top-right, InstantMessageAd
  // bottom-left, this one bottom-right.
  //
  // (A prior version of this comment claimed StickyBannerAd was also
  // bottom-right. It never was — it's top-4 left-4. Corrected here rather
  // than left to mislead the next placement decision.)
  return (
    <div
      className="fixed bottom-0 right-0 z-40 pointer-events-auto"
      style={{ width: scaledWidth, height: scaledHeight }}
    >
      {/* No close button of our own here on purpose — see the git history
          on this line for the two prior attempts (relocate the corner, then
          restyle it) that both turned out wrong. Live screenshots across
          multiple zones confirmed the ExoClick creative reliably paints its
          own real, working close button inside the iframe already; ours was
          never fixing a missing control, it was always just a second one
          stacked next to a real one. Removed rather than relocated again. */}
      <iframe
        src={exoclickIframeSrc(zoneId)}
        sandbox="allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox"
        title={t("advertisement")}
        scrolling="no"
        // scale === 1 (desktop, the overwhelmingly common case) renders
        // EXACTLY as before this mobile fix existed — width/height:100%, no
        // transform at all. A CSS transform forces GPU layer promotion,
        // which found live to sometimes break how Chrome composites a
        // cross-origin OOPIF's live video paint (confirmed via a cropped
        // screenshot: the video WAS actually there and playing, just not
        // visible in either a full-page screenshot capture or — can't fully
        // rule out — real on-screen compositing for some viewers). Since no
        // shrink is even needed at scale 1, there's no reason to carry that
        // risk on the path most people actually hit. Only when scale < 1
        // (a real mobile shrink) does this switch to native-size + transform
        // — see the matching comment removed from here, same technique as
        // NetworkDisplayAd's scale-to-fit, now scoped to when it's needed.
        style={
          scale >= 1
            ? { width: "100%", height: "100%", border: 0, display: "block", background: "transparent" }
            : {
                width: NATIVE_W,
                height: NATIVE_H,
                border: 0,
                display: "block",
                background: "transparent",
                transform: `scale(${scale})`,
                transformOrigin: "bottom left",
              }
        }
      />
    </div>
  )
}
